import apiService from '@/api'
import type {
  ContinuationDoneEnvelope,
  ContinuationProtocol,
  CreatorToolCallStreamEvent,
  StreamWireMessage,
  StreamResponseFormat,
} from '@/api/contract'

type ApiService = typeof apiService

export type {
  ContinuationDoneEnvelope,
  ContextMessage,
  StreamWireMessage,
} from '@/api/contract'

/** Metadata the relay attaches to its terminal `done` event. */
export type StreamMeta = Record<string, unknown>

/** A tool call the model emitted mid-stream. */
export type StreamToolCallEvent = CreatorToolCallStreamEvent

/**
 * A provider tool definition, opaque to the transport: it is forwarded to the
 * relay unchanged. Tool validation and execution belong to the calling loop;
 * transport does not prescribe a particular surface's tool policy.
 */
export type TransportToolDefinition = object

export interface GenerateStreamRequest {
  messages: StreamWireMessage[]
  /** Always explicit: the app resolves the bot, never the relay. */
  botId?: number | string | null
  maxTokens?: number | null
  temperature?: number
  /** Provide when the caller needs the id before the stream starts. */
  streamId?: string
  tools?: TransportToolDefinition[]
  toolChoice?: 'auto' | 'required' | 'none'
  toolProtocol?: ContinuationProtocol
  responseFormat?: StreamResponseFormat
  abortSignal?: AbortSignal
  /** Conversation this invocation belongs to, when it belongs to one. */
  conversationId?: number | string | null
}

/**
 * Stream lifecycle callbacks. Exactly one terminal callback fires per stream:
 * `onComplete` or `onError`.
 *
 * Content and reasoning arrive as both the new delta and the full accumulated
 * text, so callers never accumulate themselves.
 */
export interface StreamCallbacks {
  onStart?: (info: { streamId: string }) => void
  onConnected?: () => void
  onReasoningDelta?: (delta: string, accumulated: string) => void | Promise<void>
  onContentDelta?: (delta: string, accumulated: string) => void | Promise<void>
  onToolCallStart?: (info: { toolName: string | null }) => void | Promise<void>
  onToolCall?: (event: StreamToolCallEvent) => void | Promise<void>
  onComplete?: (result: {
    content: string
    aborted: boolean
    meta?: StreamMeta
    envelope?: ContinuationDoneEnvelope
  }) => void | Promise<void>
  onError?: (error: Error) => void | Promise<void>
}

export interface StreamHandle {
  streamId: string
  abort: () => void
  /** Settles once the stream is finished and its terminal callback has run. */
  promise: Promise<unknown>
}

interface ConversationTransportOptions {
  api?: ApiService
  logger?: Console
}

const isAbortSignal = (value: unknown): value is AbortSignal =>
  !!value
  && typeof value === 'object'
  && 'aborted' in value
  && typeof (value as AbortSignal).addEventListener === 'function'

/**
 * The single streaming client for every model invocation in the app: roleplay
 * turns, Creator turns, Open Worlds agents, and tool continuations.
 *
 * It knows nothing about surfaces, stores, persistence, message slots, or
 * temporary id conventions — callers own all of that. Its whole job is to turn a
 * request plus callbacks into a running, abortable stream with accumulated text.
 */
export class ConversationTransport {
  api: ApiService
  logger: Console

  constructor({ api = apiService, logger = console }: ConversationTransportOptions = {}) {
    this.api = api
    this.logger = logger
  }

  generateStreamId(): string {
    return `stream-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
  }

  async generateStream(
    request: GenerateStreamRequest,
    callbacks: StreamCallbacks = {},
  ): Promise<StreamHandle> {
    const streamId = request.streamId || this.generateStreamId()

    let signal: AbortSignal | undefined = request.abortSignal
    if (signal !== undefined && !isAbortSignal(signal)) {
      this.logger?.warn?.('[ConversationTransport] Ignoring a non-AbortSignal abortSignal.')
      signal = undefined
    }
    let abortController: AbortController | null = null
    if (!signal && typeof AbortController !== 'undefined') {
      abortController = new AbortController()
      signal = abortController.signal
    }

    callbacks.onStart?.({ streamId })

    let content = ''
    let reasoning = ''

    const promise = this.api.generateStream(
      {
        messages: request.messages,
        conversation_id: request.conversationId ?? null,
        ...(request.botId !== undefined ? { bot_id: request.botId } : {}),
        ...(request.maxTokens !== undefined && request.maxTokens !== null
          ? { max_tokens: request.maxTokens }
          : {}),
        ...(request.temperature !== undefined ? { temperature: request.temperature } : {}),
        stream_id: streamId,
        ...(request.tools ? { tools: request.tools } : {}),
        ...(request.toolChoice ? { tool_choice: request.toolChoice } : {}),
        ...(request.toolProtocol ? { tool_protocol: request.toolProtocol } : {}),
        ...(request.responseFormat ? { response_format: request.responseFormat } : {}),
      },
      {
        abortSignal: signal,
        onConnected: () => callbacks.onConnected?.(),
        onReasoningChunk: async (delta: string) => {
          reasoning += delta
          await callbacks.onReasoningDelta?.(delta, reasoning)
        },
        onChunk: async (delta: string) => {
          content += delta
          await callbacks.onContentDelta?.(delta, content)
        },
        onToolCallStart: async (info?: { tool_name?: string | null }) => {
          await callbacks.onToolCallStart?.({ toolName: info?.tool_name ?? null })
        },
        onToolCall: async (event: StreamToolCallEvent) => {
          await callbacks.onToolCall?.(event)
        },
        onComplete: async (
          aborted: boolean,
          meta?: StreamMeta | ContinuationDoneEnvelope,
        ) => {
          await callbacks.onComplete?.({
            content,
            aborted,
            meta,
            ...(!aborted && request.toolProtocol === 'continuation_v1' && meta
              ? { envelope: meta as ContinuationDoneEnvelope }
              : {}),
          })
        },
        onError: async (error: Error) => {
          await callbacks.onError?.(error)
        },
      },
    )

    return {
      streamId,
      abort: () => abortController?.abort(),
      promise,
    }
  }
}

export default ConversationTransport
